'use client';
import { getAddressValidation } from '@form/FormAddress';
import { Blockquote } from '@ui';
import FormInput from 'components/Form/next/FormInput';
import { TFunction } from 'i18next';
import Joi from 'joi';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export const DOMESTIC_COUNTRY = 'DE';

const COUNTRIES = [
    'DE', 'AT', 'CH', 'LI', 'LU', 'NL', 'BE', 'FR', 'IT', 'ES', 'PT', 'IE', 'DK', 'SE', 'FI', 'NO', 'IS', 'PL', 'CZ',
    'SK', 'HU', 'SI', 'HR', 'RS', 'RO', 'BG', 'GR', 'CY', 'MT', 'EE', 'LV', 'LT', 'GB', 'US'
];

export const buildInvoiceInformationSchema = (t: TFunction) => ({
    company: Joi.string().allow('', null).max(255).label(t('billing.invoice.company')),
    name: Joi.string().required().max(255).label(t('billing.invoice.name')),
    address: getAddressValidation('address', true).label(t('billing.invoice.address')),
    vat_number: Joi.string()
        .allow('', null)
        .max(255)
        .label(t('billing.invoice.vatNumber'))
        .when('address.country', {
            not: Joi.valid(DOMESTIC_COUNTRY, '', null),
            then: Joi.string().trim().required()
        })
});

export const InvoiceInformationFields = () => {
    const { t, i18n } = useTranslation('mollie');
    const country: string | undefined = useWatch({ name: 'address.country' });
    const isForeign = Boolean(country) && country !== DOMESTIC_COUNTRY;

    const options = useMemo(() => {
        let names: Intl.DisplayNames | null = null;
        try {
            names = new Intl.DisplayNames([i18n.language || 'de', 'de'], { type: 'region' });
        } catch {
            names = null;
        }
        const codes = country && !COUNTRIES.includes(country) ? [...COUNTRIES, country] : COUNTRIES;
        const [domestic, ...rest] = codes.map((code) => ({ key: code, label: names?.of(code) ?? code }));

        return [domestic, ...rest.sort((a, b) => a.label.localeCompare(b.label))];
    }, [i18n.language, country]);

    return (
        <>
            <FormInput name="company" label={t('billing.invoice.company')} fullWidth />
            <FormInput name="name" label={t('billing.invoice.nameRequired')} fullWidth />
            <FormInput.Address name="address" label={t('billing.invoice.addressRequired')} required />
            <FormInput.Select
                name="address.country"
                label={t('billing.invoice.country')}
                options={options}
                disableCustomValue
            />
            <FormInput
                name="vat_number"
                label={isForeign ? t('billing.invoice.vatNumberRequired') : t('billing.invoice.vatNumber')}
                fullWidth
            />
            {isForeign && (
                <Blockquote color="warning" size="sm" spacing>
                    {t('billing.invoice.foreignHint')}
                </Blockquote>
            )}
        </>
    );
};
